import { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '@/app/dashboard/layout';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  BarChart,
  Clock,
  Users,
  ChevronRight,
  Mic,
  FileText,
  ClipboardList,
  UserRound,
  PlusCircle,
  Stethoscope,
} from 'lucide-react';
import { EventTypesOverview } from '@/components/ui/event-types-overview';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import {
  getOrganisationDetails,
  getCurrentUser,
  getMyPatients,
  getPatientVisits,
} from '@/client/sdk.gen';
import {
  PatientRead,
  PatientVisitRead,
  VisitType,
  OrganisationResponse,
  UserResponse,
} from '@/client/types.gen';

interface StatCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon: React.ReactNode;
  loading?: boolean;
}

function StatCard({
  title,
  value,
  description,
  icon,
  loading = false,
}: StatCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
          {icon}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-20" />
        ) : (
          <div className="text-2xl font-bold">{value}</div>
        )}
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </CardContent>
    </Card>
  );
}

function QuickActionCard() {
  return (
    <Card className="col-span-2 bg-gradient-to-br from-primary/5 to-primary/10">
      <CardHeader>
        <CardTitle className="text-md">Quick Actions</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-2 sm:grid-cols-2 md:grid-cols-4">
        <Button
          variant="outline"
          className="h-auto flex-col py-4 justify-start items-start"
          asChild
        >
          <Link to="/dashboard/patients/new">
            <PlusCircle className="h-5 w-5 mb-2" />
            <span className="text-sm font-medium">New Patient</span>
            <span className="text-xs text-muted-foreground mt-1">
              Add a new patient record
            </span>
          </Link>
        </Button>

        <Button
          variant="outline"
          className="h-auto flex-col py-4 justify-start items-start"
          asChild
        >
          <Link to="/dashboard/scribe">
            <Mic className="h-5 w-5 mb-2" />
            <span className="text-sm font-medium">Start Scribing</span>
            <span className="text-xs text-muted-foreground mt-1">
              Begin a new AI recording
            </span>
          </Link>
        </Button>

        <Button
          variant="outline"
          className="h-auto flex-col py-4 justify-start items-start"
          asChild
        >
          <Link to="/dashboard/soap-notes">
            <FileText className="h-5 w-5 mb-2" />
            <span className="text-sm font-medium">SOAP Notes</span>
            <span className="text-xs text-muted-foreground mt-1">
              View & create clinical notes
            </span>
          </Link>
        </Button>

        <Button
          variant="outline"
          className="h-auto flex-col py-4 justify-start items-start"
          asChild
        >
          <Link to="/dashboard/appointments">
            <Clock className="h-5 w-5 mb-2" />
            <span className="text-sm font-medium">Appointments</span>
            <span className="text-xs text-muted-foreground mt-1">
              Manage patient visits
            </span>
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

// Map visit types to colors
const visitTypeColorMap: Record<VisitType, string> = {
  Initial: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  'Follow-up':
    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  Urgent: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  Telehealth:
    'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  Routine: 'bg-gray-100 text-gray-800 dark:bg-gray-800/50 dark:text-gray-300',
};

function RecentEncountersCard() {
  const [recentVisits, setRecentVisits] = useState<PatientVisitRead[]>([]);
  const [patientMap, setPatientMap] = useState<Record<string, PatientRead>>({});
  const [isLoading, setIsLoading] = useState(true);

  const fetchRecentEncounters = useCallback(async () => {
    setIsLoading(true);
    try {
      // Get patients where the current user is the primary physician
      const patientsResponse = await getMyPatients();

      if (patientsResponse.data) {
        const patients = patientsResponse.data;

        // Create a map of patient IDs to patient data
        const patientMapData: Record<string, PatientRead> = {};
        patients.forEach((patient: PatientRead) => {
          patientMapData[patient.id] = patient;
        });
        setPatientMap(patientMapData);

        // Collect recent visits for these patients
        const recentVisitsData: PatientVisitRead[] = [];

        // For simplicity, we'll just get visits for the first 3 patients
        const patientsToFetch = patients.slice(0, 3);

        for (const patient of patientsToFetch) {
          try {
            const visitsResponse = await getPatientVisits({
              path: {
                patient_id: patient.id,
              },
            });

            if (visitsResponse.data) {
              // Sort visits by date descending and take most recent
              const sortedVisits = visitsResponse.data.sort(
                (a: PatientVisitRead, b: PatientVisitRead) =>
                  new Date(b.visit_date).getTime() -
                  new Date(a.visit_date).getTime()
              );

              if (sortedVisits.length > 0) {
                recentVisitsData.push(sortedVisits[0]);
              }
            }
          } catch (err) {
            console.error(
              `Failed to fetch visits for patient ${patient.id}:`,
              err
            );
          }
        }

        // Sort all recent visits by date
        recentVisitsData.sort(
          (a, b) =>
            new Date(b.visit_date).getTime() - new Date(a.visit_date).getTime()
        );

        setRecentVisits(recentVisitsData.slice(0, 3)); // Limit to 3
      }
    } catch (error) {
      console.error('Failed to fetch recent encounters:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecentEncounters();
  }, [fetchRecentEncounters]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-md flex items-center justify-between">
          <span className="flex items-center">
            <Stethoscope className="mr-2 h-4 w-4 text-muted-foreground" />
            Recent Patient Encounters
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pb-2">
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : recentVisits.length > 0 ? (
          <div className="space-y-3">
            {recentVisits.map((visit) => {
              const visitDate = new Date(visit.visit_date);
              const patient = patientMap[visit.patient_id];

              return (
                <div
                  key={visit.id}
                  className="flex items-start justify-between p-2 rounded-md hover:bg-muted/50 transition-colors border border-border"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-medium">
                        {visit.chief_complaint}
                      </h3>
                      {visit.visit_type && (
                        <Badge
                          className={`${
                            visitTypeColorMap[visit.visit_type]
                          } border-0 font-normal text-xs`}
                        >
                          {visit.visit_type}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {format(visitDate, 'EEE, MMM d · h:mm a')}
                    </div>
                    {patient && (
                      <div className="text-xs mt-1">
                        <span className="text-muted-foreground">Patient:</span>{' '}
                        {patient.name}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-3 text-sm text-muted-foreground">
            <p>No recent patient encounters found</p>
            <Button variant="outline" size="sm" className="mt-2" asChild>
              <Link to="/dashboard/scribe">
                <Mic className="mr-1 h-3 w-3" />
                Start recording a visit
              </Link>
            </Button>
          </div>
        )}
      </CardContent>
      <CardFooter className="pt-2">
        <Button variant="ghost" className="w-full justify-between" asChild>
          <Link to="/dashboard/encounters">
            <span>View All Encounters</span>
            <ChevronRight className="h-4 w-4" />
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
}

function PatientInsightsCard() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-md flex items-center justify-between">
          <span className="flex items-center">
            <UserRound className="mr-2 h-4 w-4 text-muted-foreground" />
            Patient Insights
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pb-2">
        <div className="space-y-3">
          <div className="p-2 rounded-md hover:bg-muted/50 transition-colors border border-border">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">Pending Reviews</h3>
              <Badge variant="outline" className="font-normal text-xs">
                5
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              AI-generated SOAP notes awaiting your review
            </p>
          </div>

          <div className="p-2 rounded-md hover:bg-muted/50 transition-colors border border-border">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">New Alerts</h3>
              <Badge variant="outline" className="font-normal text-xs">
                3
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Patient follow-ups and medication alerts
            </p>
          </div>

          <div className="p-2 rounded-md hover:bg-muted/50 transition-colors border border-border">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">Today's Schedule</h3>
              <Badge variant="outline" className="font-normal text-xs">
                8
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Appointments scheduled for today
            </p>
          </div>
        </div>
      </CardContent>
      <CardFooter className="pt-2">
        <Button variant="ghost" className="w-full justify-between" asChild>
          <Link to="/dashboard/insights">
            <span>View All Insights</span>
            <ChevronRight className="h-4 w-4" />
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
}

export default function Page() {
  const [stats, setStats] = useState({
    totalPatients: null as number | null,
    encountersThisWeek: null as number | null,
    transcriptionAccuracy: null as number | null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [orgInfo, setOrgInfo] = useState<OrganisationResponse | null>(null);
  const [userInfo, setUserInfo] = useState<UserResponse | null>(null);

  const fetchOrganisationDetails = useCallback(async () => {
    try {
      const response = await getOrganisationDetails();
      if (response.data) {
        setOrgInfo(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch organisation details:', error);
    }
  }, []);

  const fetchUserDetails = useCallback(async () => {
    try {
      const response = await getCurrentUser();
      if (response.data) {
        setUserInfo(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch user details:', error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    try {
      // Get patients where the current user is the primary physician
      const patientsResponse = await getMyPatients();
      const totalPatients = patientsResponse.data
        ? patientsResponse.data.length
        : 0;

      // For demo purposes, we'll just simulate the other stats
      // In a real app, these would come from API calls
      const encountersThisWeek = 15;
      const transcriptionAccuracy = 98;

      setStats({
        totalPatients,
        encountersThisWeek,
        transcriptionAccuracy,
      });
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchOrganisationDetails();
    fetchUserDetails();
  }, [fetchStats, fetchOrganisationDetails, fetchUserDetails]);

  return (
    <DashboardLayout breadcrumbs={[{ label: 'Dashboard' }]}>
      <div className="flex flex-col gap-6">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold tracking-tight">
            {orgInfo ? orgInfo.name : 'Medical Dashboard'}
          </h1>
          {userInfo && (
            <p className="text-muted-foreground">
              Welcome, Dr. {userInfo.name}. Today is{' '}
              {format(new Date(), 'EEEE, MMMM d, yyyy')}.
            </p>
          )}
        </div>

        <QuickActionCard />

        <div className="grid gap-4 md:grid-cols-3">
          <StatCard
            title="Total Patients"
            value={stats.totalPatients ?? 0}
            icon={<Users className="h-4 w-4 text-primary" />}
            loading={isLoading}
          />
          <StatCard
            title="Encounters This Week"
            value={stats.encountersThisWeek ?? 0}
            description="Patient visits recorded"
            icon={<ClipboardList className="h-4 w-4 text-primary" />}
            loading={isLoading}
          />
          <StatCard
            title="Transcription Accuracy"
            value={
              stats.transcriptionAccuracy
                ? `${stats.transcriptionAccuracy}%`
                : '0%'
            }
            description="AI scribe performance"
            icon={<BarChart className="h-4 w-4 text-primary" />}
            loading={isLoading}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <RecentEncountersCard />
          <div className="grid gap-4">
            <PatientInsightsCard />
            <EventTypesOverview />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
